import Badge, { ColorType } from '@/app/common/components/Badge'
import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { InfoCircleIcon } from '@/app/common/icons/general'
import {
  GlobalAcademicTermsArray,
  ProgramTermsArray,
  ProgramTermStatus,
} from '@/queries/academic_terms/schema'
import { ProgramYearsProps } from '@/queries/program/schemas'
import { BookText, Calendar } from 'lucide-react'
import ProgramTimeline from './program-timeline'
import { ProgramPurseResource } from '@/queries/program_purses/schemas'

type Props = {
  programAcademicTerms: ProgramTermsArray
  globalAcademicTerms: GlobalAcademicTermsArray
  programSettings: ProgramYearsProps
  purse: ProgramPurseResource
  onSetupClick: () => void
}

export default function AcademicTermsView(props: Props) {
  const {
    programAcademicTerms,
    globalAcademicTerms,
    programSettings,
    purse,
    onSetupClick,
  } = props

  const selectedAcademicTerm = globalAcademicTerms.find((term) => {
    return term.id === purse.academic_term_type_id
  })

  function getSemanticColor(status: ProgramTermStatus): ColorType {
    switch (status) {
      case 'Planned':
        return 'Informative'
      case 'Active':
        return 'Positive'
      case 'Completed':
        return 'Positive'
      case 'Inactive':
        return 'Negative'

      default: {
        const exhaustiveCheck: never = status
        throw new Error(`Unhandled status: ${exhaustiveCheck}`)
      }
    }
  }

  const hasSelectedDistributionType =
    typeof purse.academic_term_type_id === 'number'
  const academicTerms = programAcademicTerms.filter((term) => {
    return (
      term.academic_term.academic_term_type_id === purse.academic_term_type_id
    )
  })

  const hasSetupAllTerms = academicTerms.every((term) => {
    return term.awarding_period_start_date && term.awarding_period_end_date
  })

  const hasActiveTerm = academicTerms.some(
    (term) => term.status_name === 'Active'
  )

  if (!hasSelectedDistributionType || !hasSetupAllTerms) {
    return (
      <div className="pt-20 max-w-lg mx-auto">
        <Card>
          <CardHeader>
            <InfoCircleIcon className="text-3xl text-primary-500" />
            <h3 className="text-2xl font-medium text-gray-800">
              Set up your academic terms
            </h3>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>
              You haven't configured your academic terms yet. Academic terms
              help organize your program's schedule and determine when awards
              are distributed to students.
            </p>
            <p>
              Get started by selecting a distribution type and setting up your
              term structure.
            </p>
            <div className="pt-6">
              <button
                onClick={onSetupClick}
                className={buttonVariants({
                  variant: 'primary',
                  size: 'lg',
                  className: 'w-full',
                })}
              >
                Set Up Academic Terms
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto w-full mt-20">
      <div className="space-y-6 mb-6">
        <Card className="overflow-hidden">
          <CardContent className="flex items-center justify-between overflow-hidden relative h-full w-full">
            <div className="absolute top-0 left-0 bg-primary-500 w-full h-1"></div>
            <div>
              <div className="text-lg font-medium text-gray-900">Term Type</div>
              <div className="text-gray-600 font-normal">
                {selectedAcademicTerm?.name}
              </div>
            </div>
            <div>
              {!hasActiveTerm && (
                <button
                  onClick={onSetupClick}
                  className={buttonVariants({
                    variant: 'outline',
                    size: 'lg',
                  })}
                >
                  Edit Term Details
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline Sidebar */}
        <div className="lg:col-span-1">
          <ProgramTimeline
            programSettings={programSettings}
            academicTerms={academicTerms}
          />
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="space-y-6">
            {/* Header Section */}
            <Card>
              <CardHeader>
                <h2 className="text-gray-900 font-semibold text-lg">
                  Award Distribution Periods - {programSettings.name}
                </h2>
                <p className="font-normal text-gray-600 text-base mt-3 ">
                  Configure when awards (scholarships, grants, financial aid)
                  can be distributed during each term. Distribution periods
                  define the active timeframe for financial aid disbursement.
                </p>
              </CardHeader>
            </Card>

            {/* Terms Grid */}
            <div className="space-y-4">
              {academicTerms.map((period, index) => {
                return (
                  <Card
                    key={period.id}
                    className="group hover:shadow-md transition-all duration-200"
                  >
                    <CardContent>
                      <div className="space-y-4">
                        {/* Header with term name and status */}
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg border-gray-200 border bg-gray-100 inline-flex items-center justify-center">
                              <BookText className="text-primary-700 size-5" />
                            </div>
                            <div>
                              <div className="text-lg font-semibold text-gray-900">
                                {period.name}
                              </div>
                              <div className="text-sm text-gray-600">
                                Term {index + 1} of {academicTerms.length}
                              </div>
                            </div>
                          </div>
                          <Badge
                            semanticColor={getSemanticColor(period.status_name)}
                          >
                            {period.status_name}
                          </Badge>
                        </div>

                        {/* Date range display */}
                        <div className="flex items-center justify-between  mt-4 pt-4">
                          <div className="flex items-center gap-3">
                            <div>
                              <div className="text-sm font-normal text-gray-600 mb-0.5">
                                Start Date
                              </div>
                              <div className="text-base font-semibold text-gray-900">
                                {period.awarding_period_start_date
                                  ? new Date(
                                      period.awarding_period_start_date
                                    ).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })
                                  : 'Not Set'}
                              </div>
                            </div>
                          </div>

                          <div className="flex  items-center gap-2 flex-1 px-8">
                            <div className="w-full h-px bg-gray-200" />
                            <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0" />
                            <div className="w-full h-px bg-gray-200" />
                          </div>

                          <div className="flex items-center gap-3">
                            <div>
                              <div className="text-sm text-right font-normal text-gray-600 mb-0.5">
                                End Date
                              </div>
                              <div className="text-base font-semibold text-gray-900">
                                {period.awarding_period_end_date
                                  ? new Date(
                                      period.awarding_period_end_date
                                    ).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })
                                  : 'Not Set'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
